import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Practice from "@/pages/Practice";
import Journal from "@/pages/Journal";
import JournalArticle from "@/pages/JournalArticle";
import Intake from "@/pages/Intake";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Portal from "@/pages/Portal";
import Admin from "@/pages/Admin";
import Locations from "@/pages/Locations";
import Location from "@/pages/Location";
import FAQ from "@/pages/FAQ";
import Bankruptcy from "@/pages/Bankruptcy";
import PrivateClient from "@/pages/PrivateClient";
import BrandKit from "@/pages/BrandKit";
import ForAdvisors from "@/pages/ForAdvisors";
import CreatorsAndAthletes from "@/pages/CreatorsAndAthletes";
import IntellectualProperty from "@/pages/IntellectualProperty";
import AssetPurchaseAgreement from "@/pages/AssetPurchaseAgreement";
import BusinessSuccession from "@/pages/BusinessSuccession";
import TexasLLC from "@/pages/TexasLLC";
import EstatePlanning from "@/pages/EstatePlanning";
import Probate from "@/pages/Probate";
import UnclaimedProperty from "@/pages/UnclaimedProperty";
import TrademarkAttorney from "@/pages/TrademarkAttorney";
import PhysicianPracticeSale from "@/pages/PhysicianPracticeSale";
import PracticeBuyIn from "@/pages/PracticeBuyIn";
import EstatePlanningFriscoPlano from "@/pages/EstatePlanningFriscoPlano";
import ForCounsel from "@/pages/ForCounsel";
import AttorneyDarryl from "@/pages/AttorneyDarryl";
import SiteMapPage from "@/pages/SiteMapPage";
import TrustsPillar from "@/pages/TrustsPillar";
import TrustService from "@/pages/TrustService";
import NotFound from "@/pages/NotFound";
import SubscribeAction from "@/pages/SubscribeAction";
import { Privacy, Terms, Disclosures } from "@/pages/Legal";
import { useEffect } from "react";
import { initAnalytics } from "@/lib/analytics";
import ErrorBoundary from "@/components/ErrorBoundary";

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="bottom-center" richColors closeButton />
            <ErrorBoundary>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/practice" element={<Practice />} />
                <Route path="/trusts" element={<TrustsPillar />} />
                <Route path="/trusts/:slug" element={<TrustService />} />
                <Route path="/practice/private-client" element={<PrivateClient />} />
                <Route path="/practice/creators-and-athletes" element={<CreatorsAndAthletes />} />
                <Route path="/practice/intellectual-property" element={<IntellectualProperty />} />
                <Route path="/business/asset-purchase-agreement-attorney-texas" element={<Navigate to="/texas-asset-purchase-agreement-attorney/" replace />} />
                <Route path="/asset-purchase-agreement-attorney" element={<Navigate to="/texas-asset-purchase-agreement-attorney/" replace />} />
                <Route path="/texas-asset-purchase-agreement-attorney" element={<Navigate to="/texas-asset-purchase-agreement-attorney/" replace />} />
                <Route path="/texas-asset-purchase-agreement-attorney/" element={<AssetPurchaseAgreement />} />
                <Route path="/business-succession-planning" element={<Navigate to="/texas-business-succession-planning-attorney/" replace />} />
                <Route path="/texas-business-succession-planning-attorney" element={<Navigate to="/texas-business-succession-planning-attorney/" replace />} />
                <Route path="/texas-business-succession-planning-attorney/" element={<BusinessSuccession />} />
                <Route path="/texas-llc-formation-attorney" element={<Navigate to="/texas-llc-formation-attorney/" replace />} />
                <Route path="/texas-llc-formation-attorney/" element={<TexasLLC />} />
                <Route path="/estate-planning" element={<Navigate to="/texas-estate-planning-attorney/" replace />} />
                <Route path="/texas-estate-planning-attorney" element={<Navigate to="/texas-estate-planning-attorney/" replace />} />
                <Route path="/texas-estate-planning-attorney/" element={<EstatePlanning />} />
                <Route path="/probate" element={<Navigate to="/texas-probate-attorney/" replace />} />
                <Route path="/texas-probate-attorney" element={<Navigate to="/texas-probate-attorney/" replace />} />
                <Route path="/texas-probate-attorney/" element={<Probate />} />
                <Route path="/texas-unclaimed-property-probate" element={<UnclaimedProperty />} />
                <Route path="/subchapter-v-bankruptcy" element={<Navigate to="/practice/subchapter-v" replace />} />
                <Route path="/texas-trademark-attorney" element={<Navigate to="/texas-trademark-attorney/" replace />} />
                <Route path="/texas-trademark-attorney/" element={<TrademarkAttorney />} />
                <Route path="/texas-physician-practice-sale-attorney" element={<Navigate to="/texas-physician-practice-sale-attorney/" replace />} />
                <Route path="/texas-physician-practice-sale-attorney/" element={<PhysicianPracticeSale />} />
                <Route path="/estate-planning-frisco-plano" element={<EstatePlanningFriscoPlano />} />
                <Route path="/blog/living-trust-funding-review" element={<Navigate to="/journal/living-trust-funding-review" replace />} />
                <Route path="/texas-professional-practice-buy-in-attorney" element={<Navigate to="/texas-professional-practice-buy-in-attorney/" replace />} />
                <Route path="/texas-professional-practice-buy-in-attorney/" element={<PracticeBuyIn />} />
                <Route path="/practice/:slug" element={<Bankruptcy />} />
                <Route path="/attorneys" element={<Navigate to="/attorneys/darryl-v-pratt" replace />} />
                <Route path="/attorneys/darryl-v-pratt" element={<AttorneyDarryl />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="/journal/:slug" element={<JournalArticle />} />
                <Route path="/intake" element={<Intake />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/locations" element={<Locations />} />
                <Route path="/locations/sugar-land" element={<Navigate to="/locations/houston-west" replace />} />
                <Route path="/locations/:slug" element={<Location />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/site-map" element={<SiteMapPage />} />
                <Route path="/sitemap" element={<Navigate to="/site-map" replace />} />
                <Route path="/subscribe/confirm/:token" element={<SubscribeAction mode="confirm" />} />
                <Route path="/subscribe/unsubscribe/:token" element={<SubscribeAction mode="unsubscribe" />} />
                <Route path="/brand" element={<BrandKit />} />
                <Route path="/for-advisors" element={<ForAdvisors />} />
                <Route path="/for-counsel" element={<ForCounsel />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/legal" element={<Disclosures />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/portal"
                  element={
                    <ProtectedRoute>
                      <Portal />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute role="admin">
                      <Admin />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            </ErrorBoundary>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
